import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQS = [
  {
    q: 'What is the difference between a Global plan and a Custom plan?',
    a: 'Global plans (Free, Pro, Growth, Enterprise) are the built-in tiers every organization can see and — if visible — subscribe to at signup. They always bill through Stripe. Custom plans are bespoke plans you create here (typically for managed-service / negotiated Enterprise deals). They are hidden from the public picker and can bypass Stripe (billed off-platform).',
  },
  {
    q: 'How do I rename a plan (e.g. Starter → Pro)?',
    a: 'On the Global Plans tab, edit the plan Name field and click Save. The new name shows everywhere (pricing page, checkout, in-app). The internal id stays the same so existing subscriptions and Stripe are never disrupted. Both Super Admins and Owners can rename plans here.',
  },
  {
    q: 'How do I create a Custom plan and assign it to an organization?',
    a: '1) Go to the Custom Plans tab → New custom plan. Set the name, price, bill credits, AI credits, trial days and overage. 2) Choose whether it bypasses Stripe (managed billing). 3) Save. 4) Go to the Org Allocation tab, pick the organization, choose the plan and click Assign. The org is switched to that plan immediately.',
  },
  {
    q: 'Why can’t I delete a plan?',
    a: 'A plan cannot be deleted while it is still assigned to one or more organizations — deleting it would break those orgs. First go to Org Allocation (or the plan’s "Assigned orgs" list), move every org off the plan, then delete it.',
  },
  {
    q: 'What does "Visible" and "Active" mean on a custom plan?',
    a: 'Visible controls whether end users can see/select the plan in the public picker (custom plans default to hidden). Active controls whether the plan can be assigned at all — an inactive plan is retained but cannot be newly assigned to an org.',
  },
  {
    q: 'What is the "Bypass Stripe" toggle?',
    a: 'When ON, the plan does NOT go through Stripe month-on-month — it is billed off-platform (managed service / invoice). When OFF, renewals are charged automatically through Stripe. Global plans always have Stripe ON (this cannot be changed); only custom plans may bypass it.',
  },
  {
    q: 'How do annual plans and renewals work?',
    a: 'Each plan can carry a monthly price and an annual price. Renewals follow the interval the customer subscribed to. AI credits and bill credits top up every month regardless of interval. Purchased credits never expire; free monthly credits reset to the plan amount each cycle.',
  },
  {
    q: 'What is the pay-per-bill rate?',
    a: 'The Global Settings pay-per-bill rate ($/bill) is what Free/Trial organizations pay to buy bill collection & parsing capacity WITHOUT subscribing to a plan. Set it once here; it applies to all self-serve credit purchases made without a subscription.',
  },
  {
    q: 'Payment / Stripe scenarios',
    a: 'Global plans: Stripe ON, self-serve checkout, auto-renew. Custom + Stripe ON: still charged via Stripe but hidden from public. Custom + Bypass: managed billing, no Stripe charge — you invoice the customer directly and the plan is simply activated on their org.',
  },
];

export default function PricingFaqDrawer({ open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="pricing-faq-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <HelpCircle className="h-5 w-5 text-primary" /> Pricing setup — FAQs
          </SheetTitle>
          <SheetDescription>
            How to set up, edit, delete and assign pricing plans, and what global vs custom means.
          </SheetDescription>
        </SheetHeader>
        <Accordion type="single" collapsible className="mt-4">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`pricing-faq-item-${i}`}>
              <AccordionTrigger className="text-left text-sm font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </SheetContent>
    </Sheet>
  );
}
